import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Moment } from 'moment-timezone'
import React from 'react'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import NumberInputField from 'src/components/NumberInputField/NumberInputField'
import Pill, { PillDesigns } from 'src/components/Pill/Pill'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import SvgNestedElementIndicator from 'src/components/Svgs/SvgNestedElementIndicator'
import TestId from 'src/components/TestId/TestId'
import { useTranslate } from 'src/localization/Trns'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { extendWeekdaysShort } from 'src/utils/date.utils'
import { css } from 'styled-components'

export type RecurrenceFormValues = {
  amountDollars?: number
  recurrenceDollars?: number
  period?: RecurrencePeriod
  interval?: number
  terminator?: RecurrenceTerminator
  count?: number
  untilMoment?: Moment
  byWeekDay?: number
  byMonthDay?: number
  byYearDay?: number
}

export type RecurrenceFormErrors = Partial<{
  [field in keyof RecurrenceFormValues]: Array<React.ReactElement | string>
}>

export interface RecurrenceFormChangeHandlers {
  onRecurrenceDollarsChange?: (val?: number) => void
  onPeriodChange: (val: RecurrencePeriod) => void
  onIntervalChange: (val: number) => void
  onTerminatorChange: (val: RecurrenceTerminator) => void
  onCountChange: (val: number) => void
  onUntilMomentChange: (val: Moment) => void
  onByWeekDayChange: (val: number) => void
  onByMonthDayChange: (val: number) => void
}

interface RecurrenceFormProps {
  className?: string
  recurrenceValues: RecurrenceFormValues
  recurrenceErrors: RecurrenceFormErrors
  recurrenceChangeHandlers: RecurrenceFormChangeHandlers
}

const RecurrenceForm: React.FC<RecurrenceFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const intervalIsDaily =
    props.recurrenceValues.period === RecurrencePeriod.Daily
  const intervalIsWeekly =
    props.recurrenceValues.period === RecurrencePeriod.Weekly
  const intervalIsMonthly =
    props.recurrenceValues.period === RecurrencePeriod.Monthly

  const terminatorIsNone =
    props.recurrenceValues.terminator === RecurrenceTerminator.None
  const terminatorIsDate =
    props.recurrenceValues.terminator === RecurrenceTerminator.Date
  const terminatorIsCount =
    props.recurrenceValues.terminator === RecurrenceTerminator.Count

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <FieldLabel>{translate('Frequency')}</FieldLabel>
      <div>
        <Pill
          design={PillDesigns.Regular}
          selected={intervalIsDaily}
          handleClick={(): void => {
            props.recurrenceChangeHandlers.onPeriodChange(
              RecurrencePeriod.Daily,
            )
          }}
        >
          <TestId testId={'daily'}>{translate('Daily')}</TestId>
        </Pill>
        <Pill
          design={PillDesigns.Regular}
          selected={intervalIsWeekly}
          handleClick={(): void => {
            props.recurrenceChangeHandlers.onPeriodChange(
              RecurrencePeriod.Weekly,
            )
          }}
        >
          <TestId testId={'weekly'}>{translate('Weekly')}</TestId>
        </Pill>
        <Pill
          design={PillDesigns.Regular}
          selected={intervalIsMonthly}
          handleClick={(): void => {
            props.recurrenceChangeHandlers.onPeriodChange(
              RecurrencePeriod.Monthly,
            )
          }}
        >
          <TestId testId={'monthly'}>{translate('Monthly')}</TestId>
        </Pill>
      </div>
      {props.recurrenceErrors.period !== undefined && (
        <ErrorList
          css={css`
            margin-top: 4px;
          `}
          errors={props.recurrenceErrors.period}
        />
      )}
      {props.recurrenceValues.period && (
        <div
          css={css`
            display: flex;
            margin-top: 12px;
          `}
        >
          <SvgNestedElementIndicator />
          <div
            css={css`
              margin-left: 10px;
              margin-top: 6px;
              display: flex;
              flex-direction: column;
            `}
          >
            <FieldLabel>{translate('Reset every')}</FieldLabel>
            <div
              css={css`
                display: flex;
                align-items: center;
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <TestId testId={'resetEvery'}>
                <NumberInputField
                  maxValue={9999}
                  css={css`
                    max-width: 80px;
                    margin-right: 10px;
                  `}
                  value={props.recurrenceValues.interval}
                  onChange={props.recurrenceChangeHandlers.onIntervalChange}
                  errors={props.recurrenceErrors.interval}
                  testId="resetEveryField"
                />
              </TestId>
              {intervalIsDaily &&
                (props.recurrenceValues.interval === 1
                  ? translate('day')
                  : translate('days'))}
              {intervalIsWeekly &&
                (props.recurrenceValues.interval === 1
                  ? translate('week')
                  : translate('weeks'))}
              {intervalIsMonthly &&
                (props.recurrenceValues.interval === 1
                  ? translate('month')
                  : translate('months'))}
            </div>
            {(intervalIsWeekly || intervalIsMonthly) && (
              <div
                css={css`
                  margin-top: 24px;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <FieldLabel>{translate('Repeats on')}</FieldLabel>
                {intervalIsWeekly && (
                  <div>
                    {extendWeekdaysShort().map((wd) => (
                      <Pill
                        design={PillDesigns.Circular}
                        key={wd.index}
                        selected={props.recurrenceValues.byWeekDay === wd.index}
                        handleClick={(): void => {
                          props.recurrenceChangeHandlers.onByWeekDayChange(
                            wd.index,
                          )
                        }}
                      >
                        {wd.weekday}
                      </Pill>
                    ))}
                    {props.recurrenceErrors.byWeekDay !== undefined && (
                      <ErrorList
                        css={css`
                          margin-top: 4px;
                        `}
                        errors={props.recurrenceErrors.byWeekDay}
                      />
                    )}
                  </div>
                )}
                {intervalIsMonthly && (
                  <div
                    css={css`
                      display: flex;
                      flex-wrap: wrap;
                      max-width: 350px;
                    `}
                  >
                    {new Array(31).fill(undefined).map((_, i) => (
                      <button
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.regular,
                          )};
                          height: 50px;
                          width: 50px;
                          background-color: ${styleConstants.colors
                            .gray_lightest};
                          border: none;
                          cursor: pointer;

                          box-shadow:
                            1px 0 0 0 ${styleConstants.colors.gray_medium},
                            0 1px 0 0 ${styleConstants.colors.gray_medium},
                            1px 0 0 0 ${styleConstants.colors.gray_medium} inset,
                            0 1px 0 0 ${styleConstants.colors.gray_medium} inset;

                          &:hover {
                            background-color: ${styleConstants.colors.white};
                          }

                          ${props.recurrenceValues.byMonthDay === i + 1 &&
                          css`
                            background-color: ${themeConstants.colors.cta};
                            ${getFontCss(
                              14,
                              styleConstants.colors.white,
                              styleConstants.fontFamilies.regular,
                            )};

                            &:hover {
                              background-color: ${themeConstants.colors
                                .cta_dark};
                            }
                          `}
                        `}
                        key={i}
                        type={'button'}
                        onClick={(): void => {
                          props.recurrenceChangeHandlers.onByMonthDayChange(
                            i + 1,
                          )
                        }}
                      >
                        {i + 1}
                      </button>
                    ))}
                    {props.recurrenceErrors.byMonthDay !== undefined && (
                      <ErrorList
                        css={css`
                          margin-top: 4px;
                        `}
                        errors={props.recurrenceErrors.byMonthDay}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div
        css={css`
          margin-top: 24px;
          display: flex;
          flex-direction: column;
        `}
      >
        <FieldLabel>{translate('Reset Ends')}</FieldLabel>
        <div>
          <Pill
            design={PillDesigns.Regular}
            selected={terminatorIsNone}
            handleClick={(): void => {
              props.recurrenceChangeHandlers.onTerminatorChange(
                RecurrenceTerminator.None,
              )
            }}
          >
            <TestId testId={'neverEnds'}>{translate('Never Ends')}</TestId>
          </Pill>
          <Pill
            design={PillDesigns.Regular}
            selected={terminatorIsDate}
            handleClick={(): void => {
              props.recurrenceChangeHandlers.onTerminatorChange(
                RecurrenceTerminator.Date,
              )
            }}
          >
            {translate('On Date')}
          </Pill>
          <Pill
            design={PillDesigns.Regular}
            selected={terminatorIsCount}
            handleClick={(): void => {
              props.recurrenceChangeHandlers.onTerminatorChange(
                RecurrenceTerminator.Count,
              )
            }}
          >
            {translate('After Number of Resets')}
          </Pill>
        </div>
        {props.recurrenceErrors.terminator !== undefined && (
          <ErrorList
            css={css`
              margin-top: 4px;
            `}
            errors={props.recurrenceErrors.terminator}
          />
        )}
        {(terminatorIsCount || terminatorIsDate) && (
          <div
            css={css`
              display: flex;
              margin-top: 12px;
            `}
          >
            <SvgNestedElementIndicator />
            {terminatorIsDate && (
              <>
                <SingleDateInputField
                  css={css`
                    margin-left: 10px;
                    margin-top: 6px;
                  `}
                  fieldLabel={translate('End Date')}
                  onDateMomentChange={(val: Moment | undefined): void => {
                    if (val !== undefined) {
                      props.recurrenceChangeHandlers.onUntilMomentChange(val)
                    }
                  }}
                  dateMoment={props.recurrenceValues.untilMoment}
                />
                {props.recurrenceErrors.untilMoment !== undefined && (
                  <ErrorList
                    css={css`
                      margin-top: 4px;
                    `}
                    errors={props.recurrenceErrors.untilMoment}
                  />
                )}
              </>
            )}
            {terminatorIsCount && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  margin-left: 10px;
                  margin-top: 6px;
                `}
              >
                <FieldLabel>{translate('Number of Resets')}</FieldLabel>
                <TestId testId={'numberOfResets'}>
                  <NumberInputField
                    maxValue={9999}
                    css={css`
                      max-width: 80px;
                    `}
                    value={props.recurrenceValues.count}
                    errors={props.recurrenceErrors.count}
                    onChange={props.recurrenceChangeHandlers.onCountChange}
                  />
                </TestId>
                {props.recurrenceErrors.count !== undefined && (
                  <ErrorList
                    css={css`
                      margin-top: 4px;
                    `}
                    errors={props.recurrenceErrors.count}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {/* TODO: summary sentences */}
    </div>
  )
}

export default RecurrenceForm
